// Eskom PARCC — Working Papers list (read-only).
//
// The monolith's Working Paper page (`renderWorkingPaperPage`), rebuilt: the
// list of *allocated* transactions, each joined to its working paper (by
// `referenceNo`) for the captured review status / compliance / notes.
//
// Role scope is the security boundary (see `scopeWorkingPapers`): a reviewer
// sees ONLY transactions allocated to their own email; an admin sees every
// allocated transaction. Scoping runs on the typed data via the pure helper, so
// the rule is testable in isolation and identical for both roles' code paths.
//
// WRITE: an assigned reviewer (or an admin) may capture/edit a row's review
// fields. Two affordances per editable row: "Edit" opens the lightweight review-
// field editor (status / notes / compliance — see WorkingPaperEditor), and "CES"
// opens the full Compliance Evaluation Sheet (Task 19). Both are gated on
// `canEditTransaction`; a non-permitted user keeps the read-only row.
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Pager,
  SmartList,
  usePagination,
  StatusIcon,
  StatusLegend,
  PersonChip,
  WorkingPaperNotes,
  ComplianceBadge,
  Truncated,
  Button,
  LoadingState,
  ErrorState,
  EmptyState,
  useToast,
  type SortOption,
} from '../../components/ui';
import {
  useTransactions,
  useWorkingPapers,
  useCesRowsByRef,
  useFindings,
  useNameDirectory,
  useQaNotes,
  useAddQaNote,
  useResolveQaNote,
} from '../../hooks';
import { useAuth } from '../../lib/auth';
import type { NameDirectory } from '../../lib/people';
import { unionOptions } from '../../lib/options';
import {
  buildComplianceSheets,
  buildWorkingPaperCriteriaSheet,
  buildWorkingPaperDocumentSheets,
  selectReviewedTransactions,
  workbookFromSheets,
  workbookFromFilterSheet,
  workbookFromDocuments,
  downloadWorkbook,
} from '../exports';
import { REVIEW_STATUS_OPTIONS } from '../../domain/enums';
import { canEditTransaction } from '../transactions/editTransaction';
import {
  canDownloadWorkingPaper,
  canBulkDownloadWorkingPapers,
  canUploadCesWorkbook,
  canViewAllDivisions,
  canQaWorkingPaper,
} from '../../lib/permissions';
import { WorkingPaperEditor } from './WorkingPaperEditor';
import { QaNotesPanel } from './QaNotesPanel';
import { splitFindingDisplay } from '../findings/findingDisplay';
import { genuineFindings } from '../../domain/findings';
import { hasRealLink } from '../../domain/links';
import { effectiveFindingCompliance } from '../findings/filterFindings';
import type { QaNoteRead } from '../../lib/db';
import {
  scopeWorkingPapers,
  filterWorkingPapers,
  effectiveReviewStatus,
  effectiveWorkingPaperCompliance,
  workingPaperNotes,
  EMPTY_WORKING_PAPER_FILTERS,
  type WorkingPaperFilters as Filters,
  type WorkingPaperRow,
} from './filterWorkingPapers';
import { WorkingPaperFilters } from './WorkingPaperFilters';

const EMPTY_DIRECTORY: NameDirectory = new Map();

const WP_SORT_OPTIONS: SortOption<WorkingPaperRow>[] = [
  { key: 'reference', label: 'Reference', accessor: (r) => r.tx.referenceNo },
  { key: 'supplier', label: 'Supplier', accessor: (r) => r.tx.supplier ?? '' },
  { key: 'division', label: 'Division', accessor: (r) => r.tx.division ?? '' },
  { key: 'reviewStatus', label: 'Review status', accessor: (r) => effectiveReviewStatus(r) },
  { key: 'compliance', label: 'Compliance', accessor: (r) => effectiveWorkingPaperCompliance(r) },
];

export function WorkingPapersPage(): React.JSX.Element {
  const { grant } = useAuth();
  const seesAllDivisions = grant ? canViewAllDivisions(grant.role) : false;
  const canBulkDownload = grant != null && canBulkDownloadWorkingPapers(grant.role);
  const transactionsQuery = useTransactions();
  const workingPapersQuery = useWorkingPapers();
  const directoryQuery = useNameDirectory();
  // CES rows (grouped by reference) feed the per-route compliance workbook.
  const cesRowsByRefQuery = useCesRowsByRef();
  // Findings feed the reviewed working-paper DOCUMENT export (admin gets all).
  const findingsQuery = useFindings();
  const { show } = useToast();
  const [filters, setFilters] = useState<Filters>(EMPTY_WORKING_PAPER_FILTERS);
  // The reference of the row whose CES capture modal is open (null = closed).
  // The reference of the row whose review-field editor is open (null = closed).
  const [editingRef, setEditingRef] = useState<string | null>(null);
  const navigate = useNavigate();
  // The reference of the row expanded in-place (full notes + capture actions).
  const [expandedRef, setExpandedRef] = useState<string | null>(null);

  // Visibility boundary (capability model): cross-division roles see every
  // allocated working paper; teamLead/reviewer see their division's (∪ their own).
  const rows = useMemo<WorkingPaperRow[]>(
    () => scopeWorkingPapers(transactionsQuery.data ?? [], grant, workingPapersQuery.data ?? []),
    [transactionsQuery.data, workingPapersQuery.data, grant],
  );

  const reviewStatusOptions = useMemo(
    () => unionOptions(REVIEW_STATUS_OPTIONS, rows.map(effectiveReviewStatus)),
    [rows],
  );
  // Feedback 2b — buyer + individual-user (assignee) options, from the scoped rows.
  const buyerOptions = useMemo(
    () => unionOptions([], rows.map((r) => r.tx.buyer ?? '').filter(Boolean)),
    [rows],
  );
  const userOptions = useMemo(
    () => unionOptions([], rows.map((r) => r.tx.assignedToEmail ?? '').filter(Boolean)),
    [rows],
  );

  const filtered = useMemo(() => filterWorkingPapers(rows, filters), [rows, filters]);

  // The reference→working-paper join for the flat working-paper export.
  const wpByRef = useMemo(() => {
    const m = new Map<string, NonNullable<WorkingPaperRow['wp']>>();
    for (const r of filtered) if (r.wp) m.set(r.tx.referenceNo, r.wp);
    return m;
  }, [filtered]);

  // The reviewed-document export spans ALL reviewed papers (filter-independent),
  // so it joins against every working paper / finding — not just the filtered view.
  const allWorkingPapers = workingPapersQuery.data;
  const allWpByRef = useMemo(() => {
    const m = new Map<string, NonNullable<typeof allWorkingPapers>[number]>();
    for (const wp of allWorkingPapers ?? []) m.set(wp.referenceNo, wp);
    return m;
  }, [allWorkingPapers]);

  const allFindings = findingsQuery.data;
  const findingsByRef = useMemo(() => {
    const m = new Map<string, NonNullable<typeof allFindings>>();
    for (const f of allFindings ?? []) {
      const ref = f.referenceNo;
      if (!ref) continue;
      const bucket = m.get(ref);
      if (bucket) bucket.push(f);
      else m.set(ref, [f]);
    }
    return m;
  }, [allFindings]);

  // QA notes (4a): the thread shown in each working paper's expander. Managers/
  // admins may add + resolve; everyone who can view the paper sees the thread.
  const qaNotesQuery = useQaNotes();
  const addQaNote = useAddQaNote();
  const resolveQaNote = useResolveQaNote();
  const canQa = grant ? canQaWorkingPaper(grant.role) : false;
  const qaNotesByRef = useMemo(() => {
    const m = new Map<string, QaNoteRead[]>();
    for (const n of qaNotesQuery.data ?? []) {
      if (!n.referenceNo) continue;
      const bucket = m.get(n.referenceNo);
      if (bucket) bucket.push(n);
      else m.set(n.referenceNo, [n]);
    }
    return m;
  }, [qaNotesQuery.data]);

  // ISO date stamp for the filename, e.g. 2026-06-01 (deterministic per call).
  const stamp = () => new Date().toISOString().slice(0, 10);

  // Admin-only: the per-route compliance evaluation working paper (one sheet per
  // procurement route), built from the scoped transactions + their CES rows.
  const downloadComplianceWorkbook = useCallback(() => {
    try {
      const txs = filtered.map((r) => r.tx);
      const sheets = buildComplianceSheets(txs, cesRowsByRefQuery.data ?? new Map());
      if (sheets.length === 0) {
        show('There are no transactions to export.', 'info');
        return;
      }
      downloadWorkbook(
        `compliance_evaluation_working_paper_${stamp()}.xlsx`,
        workbookFromSheets(sheets),
      );
      show(
        `Downloaded ${txs.length.toLocaleString('en-ZA')} transaction(s) across ${sheets.length} route sheet(s).`,
        'success',
      );
    } catch (err) {
      show(
        `Could not download the compliance workbook: ${err instanceof Error ? err.message : String(err)}`,
        'error',
      );
    }
  }, [filtered, cesRowsByRefQuery.data, show]);

  // Admin-only (owner feedback point 7): the FULL working-paper document for ALL
  // reviewed transactions — independent of the on-screen filter, so the admin gets
  // every reviewed working paper in one workbook. Each paper renders as a complete
  // document (header + review block + full checklist + findings); the checklist
  // comes from CES rows, or is parsed back from the legacy text-blob.
  const downloadReviewedWorkingPapers = useCallback(() => {
    try {
      const reviewed = selectReviewedTransactions(transactionsQuery.data ?? []);
      const sheets = buildWorkingPaperDocumentSheets(
        reviewed,
        allWpByRef,
        cesRowsByRefQuery.data ?? new Map(),
        findingsByRef,
      );
      if (sheets.length === 0) {
        show('There are no reviewed transactions to export.', 'info');
        return;
      }
      downloadWorkbook(`reviewed_working_papers_${stamp()}.xlsx`, workbookFromDocuments(sheets));
      show(
        `Downloaded ${reviewed.length.toLocaleString('en-ZA')} reviewed working paper(s) across ${sheets.length} route sheet(s).`,
        'success',
      );
    } catch (err) {
      show(
        `Could not download the reviewed working papers: ${err instanceof Error ? err.message : String(err)}`,
        'error',
      );
    }
  }, [transactionsQuery.data, allWpByRef, cesRowsByRefQuery.data, findingsByRef, show]);

  // The filter-friendly working-paper export (9 Jun call): one row per CES
  // criterion with the transaction + grey-header context repeated, AutoFilter on
  // the header. Available to reviewers for their own allocated rows; admins all.
  const downloadWorkingPaper = useCallback(() => {
    try {
      const txs = filtered.map((r) => r.tx);
      if (txs.length === 0) {
        show('There are no working-paper rows to download.', 'info');
        return;
      }
      const sheet = buildWorkingPaperCriteriaSheet(
        txs,
        wpByRef,
        cesRowsByRefQuery.data ?? new Map(),
        findingsByRef,
      );
      downloadWorkbook(
        `compliance_review_working_paper_${stamp()}.xlsx`,
        workbookFromFilterSheet('Working Paper', sheet),
      );
      show(
        `Downloaded ${(sheet.length - 1).toLocaleString('en-ZA')} working-paper row(s).`,
        'success',
      );
    } catch (err) {
      show(
        `Could not download the working paper: ${err instanceof Error ? err.message : String(err)}`,
        'error',
      );
    }
  }, [filtered, wpByRef, cesRowsByRefQuery.data, findingsByRef, show]);

  // Single-transaction export — "drill down to a specific transaction" without
  // touching the page filter: the same filterable sheet, one transaction only.
  const downloadOneWorkingPaper = useCallback(
    (referenceNo: string) => {
      try {
        const row = rows.find((r) => r.tx.referenceNo === referenceNo);
        if (!row) return;
        const sheet = buildWorkingPaperCriteriaSheet(
          [row.tx],
          wpByRef,
          cesRowsByRefQuery.data ?? new Map(),
          findingsByRef,
        );
        downloadWorkbook(
          `working_paper_${referenceNo}_${stamp()}.xlsx`,
          workbookFromFilterSheet('Working Paper', sheet),
        );
        show(`Downloaded the working paper for ${referenceNo}.`, 'success');
      } catch (err) {
        show(
          `Could not download the working paper: ${err instanceof Error ? err.message : String(err)}`,
          'error',
        );
      }
    },
    [rows, wpByRef, cesRowsByRefQuery.data, findingsByRef, show],
  );

  const directory = directoryQuery.data ?? EMPTY_DIRECTORY;

  /** A working paper as a compound row, with an inline expander for full notes. */
  const renderWpRow = (r: WorkingPaperRow): React.JSX.Element => {
    const ref = r.tx.referenceNo;
    const expanded = ref === expandedRef;
    const canEdit = canEditTransaction(r.tx, grant);
    const meta = [r.tx.dataset, r.tx.division].filter(
      (v): v is string => typeof v === 'string' && v.length > 0,
    );
    const notes = workingPaperNotes(r);
    // Data persistence (Lindiwe feedback Jun 23 2026): the captured FINDINGS must
    // remain visible in the working-paper UI after a review — previously they were
    // only in the register / XLSX, so the paper looked "cleared". Surface them here,
    // counted the way the register counts (genuine findings — one per criterion, no
    // "Additional comments" notes; owner feedback Aug 2026, the count read inflated).
    const rowFindings = genuineFindings(findingsByRef.get(ref) ?? []);
    return (
      <>
        <span className="srow-lead">
          <StatusIcon value={effectiveReviewStatus(r)} />
          <StatusIcon value={effectiveWorkingPaperCompliance(r) || 'Pending Review'} />
        </span>
        <span className="srow-main">
          <span className="srow-line1">
            {ref ? (
              <Link
                className="srow-ref"
                to={`/transactions?ref=${encodeURIComponent(ref)}`}
                onClick={(e) => e.stopPropagation()}
              >
                {ref}
              </Link>
            ) : (
              <span className="srow-ref">—</span>
            )}
            <span className="srow-supplier">{r.tx.supplier ?? '—'}</span>
          </span>
          <span className="srow-line2">
            <span>{meta.join(' · ') || '—'}</span>
            {r.tx.assignedToEmail && (
              <>
                <span className="srow-sep">·</span>
                <PersonChip email={r.tx.assignedToEmail} directory={directory} />
              </>
            )}
          </span>
          {!expanded && notes && <WorkingPaperNotes text={notes} clamped />}
        </span>
        <span className="srow-actions">
          <span className="srow-chevron" aria-hidden>
            {expanded ? '▾' : '▸'}
          </span>
        </span>
        {expanded && (
          <div className="srow-expand">
            {notes ? (
              <WorkingPaperNotes text={notes} />
            ) : (
              <span className="small">No working-paper notes captured yet.</span>
            )}
            {/* Captured findings stay visible in the UI after review (data
                persistence — no need to download the spreadsheet to read them). */}
            {rowFindings.length > 0 && (
              <div className="wp-captured-findings" style={{ marginTop: 12 }}>
                <div className="small" style={{ fontWeight: 700, marginBottom: 6 }}>
                  Captured findings ({rowFindings.length})
                </div>
                <ul
                  style={{
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  {rowFindings.map((f) => {
                    const { procedure, note } = splitFindingDisplay(f);
                    return (
                      <li key={f.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <ComplianceBadge
                          level={effectiveFindingCompliance(f) || 'Pending Review'}
                        />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          {procedure && <b>{procedure} — </b>}
                          <Truncated text={note || '—'} max={320} />
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {/* QA notes thread (4a): visible to all who can view the paper; managers
                + admins may add / resolve. The reviewer is alerted server-side. */}
            <QaNotesPanel
              notes={qaNotesByRef.get(ref) ?? []}
              canQa={canQa}
              directory={directory}
              busy={addQaNote.isPending || resolveQaNote.isPending}
              onAdd={(note) => ref && addQaNote.mutate({ referenceNo: ref, note })}
              onResolve={(id, resolved) => resolveQaNote.mutate({ id, resolved })}
            />
            <div className="actions" style={{ marginTop: 10 }} onClick={(e) => e.stopPropagation()}>
              {canEdit && <Button onClick={() => setEditingRef(ref)}>Edit</Button>}
              {canEdit && (
                <Button onClick={() => navigate(`/ces/${encodeURIComponent(ref)}`)}>CES</Button>
              )}
              {hasRealLink(r.tx) && (
                // A6: the OpenText document folder from the working-paper view
                // (requires Microsoft Edge on the Eskom network). Gated on a REAL
                // link so a placeholder cell never renders a dead anchor.
                <a className="btn" href={r.tx.link} target="_blank" rel="noopener noreferrer">
                  OpenText
                </a>
              )}
              <Button onClick={() => ref && downloadOneWorkingPaper(ref)}>Export</Button>
            </div>
          </div>
        )}
      </>
    );
  };

  // Likewise for the review-field editor, resolved from the scoped rows so the
  // permission check sees the same allocation the row carries.
  const editingRow = useMemo(
    () => (editingRef ? (rows.find((r) => r.tx.referenceNo === editingRef) ?? null) : null),
    [editingRef, rows],
  );

  // Findings are part of the load gate too: the review-field editor now captures
  // findings and Save replaces ALL findings for the tx (delete-then-reinsert), so
  // it must seed from the LIVE finding set — opening it before findings load would
  // seed empty and silently wipe existing findings on Save.
  // Bound the list — both themes get a pager (Lumen 7–8/page, Legacy 50).
  const pagination = usePagination(filtered);

  const isLoading =
    transactionsQuery.isLoading || workingPapersQuery.isLoading || findingsQuery.isLoading;
  // findingsQuery.error MUST gate the page: the editor seeds its draft from the
  // live finding set and Save replaces ALL findings (delete-then-reinsert), so a
  // failed findings fetch that fell through to render would seed empty and
  // silently WIPE the transaction's real findings on the next Save.
  const error = transactionsQuery.error ?? workingPapersQuery.error ?? findingsQuery.error;

  if (isLoading) return <LoadingState message="Loading working papers…" />;
  if (error) {
    return (
      <ErrorState
        message="Could not load the working papers."
        detail={error instanceof Error ? error.message : String(error)}
        onRetry={() => {
          void transactionsQuery.refetch();
          void workingPapersQuery.refetch();
        }}
      />
    );
  }
  if (rows.length === 0) {
    return (
      <EmptyState
        message={
          seesAllDivisions
            ? 'No transactions have been allocated for review yet.'
            : 'No transactions are currently allocated in your division.'
        }
      />
    );
  }

  return (
    <>
      <WorkingPaperFilters
        filters={filters}
        onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
        onReset={() => setFilters(EMPTY_WORKING_PAPER_FILTERS)}
        reviewStatusOptions={reviewStatusOptions}
        buyerOptions={buyerOptions}
        userOptions={userOptions}
      />

      <div className="card">
        <div className="section-head">
          <div>
            <h3>Working Papers</h3>
            <StatusLegend kind="compliance" />
          </div>
          <div className="actions">
            {canUploadCesWorkbook(grant) && (
              <Button
                size="sm"
                onClick={() => navigate('/ces-upload')}
                title="Upload a completed CES working paper workbook (Excel)"
              >
                Upload completed papers
              </Button>
            )}
            {canBulkDownload && (
              <>
                <Button
                  size="sm"
                  onClick={downloadReviewedWorkingPapers}
                  title="Download all reviewed working papers (XLSX)"
                >
                  All reviewed (XLSX)
                </Button>
                <Button
                  size="sm"
                  onClick={downloadComplianceWorkbook}
                  disabled={filtered.length === 0}
                  title="Download compliance workbook (XLSX)"
                >
                  Compliance (XLSX)
                </Button>
              </>
            )}
            {grant != null && canDownloadWorkingPaper(grant.role) && (
              <Button
                size="sm"
                onClick={downloadWorkingPaper}
                disabled={filtered.length === 0}
                title="Download working paper (XLSX)"
              >
                This paper (XLSX)
              </Button>
            )}
          </div>
        </div>
        <SmartList
          items={pagination.slice}
          getRowKey={(r) => r.tx.referenceNo || r.tx.id}
          renderRow={renderWpRow}
          sortOptions={WP_SORT_OPTIONS}
          initialSortKey="reference"
          onRowClick={(r) =>
            setExpandedRef((cur) => (cur === r.tx.referenceNo ? null : r.tx.referenceNo))
          }
          selectedRowKey={expandedRef ?? undefined}
          countLabel={
            filtered.length === rows.length
              ? `${rows.length.toLocaleString('en-ZA')} working paper(s)`
              : `${filtered.length.toLocaleString('en-ZA')} of ${rows.length.toLocaleString('en-ZA')} working paper(s)`
          }
          emptyMessage="No working papers match the filters."
        />
        <Pager pagination={pagination} noun="working paper" />
        <p className="small" style={{ marginTop: 12 }}>
          Select a row to see its full notes and capture controls. Use CES to capture or update a
          transaction&apos;s Compliance Evaluation Sheet.
        </p>
      </div>

      {/* Review-field editor for the chosen allocated transaction. Gated on
          canEditTransaction so a reviewer can only edit their own allocations. */}
      {editingRow && canEditTransaction(editingRow.tx, grant) && (
        <WorkingPaperEditor
          tx={editingRow.tx}
          wp={editingRow.wp}
          findings={findingsByRef.get(editingRow.tx.referenceNo) ?? []}
          capturedByEmail={grant?.email ?? ''}
          onClose={() => setEditingRef(null)}
        />
      )}
    </>
  );
}
